import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound, MailCheck, Loader2 } from 'lucide-react';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import LegalFooter from '../components/LegalFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';

/**
 * ForgotPasswordPage — Phase 27.6 (P0.1)
 *
 * Public page. The user types their email; backend always returns ok
 * (no email enumeration). We always show the same success message.
 */
const ForgotPasswordPage = () => {
  const { t } = useTranslation('auth');
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', {
        email: v,
        reset_url_base: `${window.location.origin}/reset-password`,
      });
      setSent(true);
    } catch (err) {
      toast({
        title: t('forgot_password.error_title'),
        description: err.response?.data?.detail || t('forgot_password.error_default'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      <div className="mb-4 flex w-full max-w-md justify-end">
        <LanguageSwitcher variant="full" align="end" testId="forgot-language-switcher" />
      </div>
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="forgot-password-card">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15">
            <KeyRound className="h-7 w-7 text-amber-600" strokeWidth={1.8} />
          </div>
          <h1 className="text-center text-2xl font-bold text-foreground">{t('forgot_password.title')}</h1>

          {!sent ? (
            <>
              <p className="mt-2 text-center text-sm text-muted-foreground">
                {t('forgot_password.subtitle')}
              </p>
              <form onSubmit={onSubmit} className="mt-6 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-foreground">{t('forgot_password.email_label')}</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder={t('forgot_password.email_placeholder')}
                    className="h-10 bg-background"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="gold-gradient h-11 w-full text-base font-semibold"
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MailCheck className="mr-2 h-4 w-4" />}
                  {loading ? t('forgot_password.submitting') : t('forgot_password.submit')}
                </Button>
              </form>
            </>
          ) : (
            <div className="mt-4 text-center" data-testid="forgot-password-success">
              <p className="text-sm text-foreground">
                {t('forgot_password.sent_title', { email })}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                {t('forgot_password.sent_hint')}
              </p>
              <Button
                onClick={() => { setSent(false); setEmail(''); }}
                variant="ghost"
                className="mt-4 text-xs"
                data-testid="forgot-password-retry-btn"
              >
                {t('forgot_password.retry')}
              </Button>
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-xs">
          <Link to="/login" className="font-semibold text-muted-foreground hover:underline" data-testid="forgot-password-back-login">
            {t('forgot_password.back_to_login')}
          </Link>
        </div>
      </div>
      <LegalFooter className="mt-8 max-w-md px-4" />
    </div>
  );
};

export default ForgotPasswordPage;
